import React, { useState, useEffect } from 'react';
import { Card, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { GiChicken, GiSittingDog, GiCow, GiSheep } from 'react-icons/gi';
import { FaRupeeSign, FaUndo, FaFilter, FaMapMarkerAlt, FaBirthdayCake, FaWeight, FaList, FaPaw } from 'react-icons/fa';
import { getCitiesByState } from '../services/locationService';
import './FilterSidebar.css';
import { Range } from 'react-range';
import { sanitizeInput } from '../utils/security';

const categoryConfigs = {
  chickens: {
    label: 'Chickens',
    icon: <GiChicken aria-label="Chicken" />,
    fields: [
      { name: 'type', label: 'Type', type: 'select', options: ['Hen', 'Rooster', 'Chicks', 'Breeding Pair'] },
      { name: 'weight', label: 'Weight (kg)', type: 'number', icon: <FaWeight /> }
    ]
  },
  pets: {
    label: 'Pets',
    icon: <GiSittingDog aria-label="Dog" />,
    fields: [
      { name: 'type', label: 'Animal Type', type: 'select', options: ['Dog', 'Cat', 'Rabbit', 'Bird', 'Other'] },
      { name: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female'] }
    ]
  },
  cattle: {
    label: 'Cattle',
    icon: <GiCow aria-label="Cow" />,
    fields: [
      { name: 'type', label: 'Type', type: 'select', options: ['Cow', 'Buffalo', 'Bull', 'Calf'] },
      { name: 'milkCapacity', label: 'Milk Capacity (litres/day)', type: 'number', icon: <FaWeight /> }
    ]
  },
  'sheep-goat': {
    label: 'Sheep/Goat',
    icon: <GiSheep aria-label="Sheep/Goat" />,
    fields: [
      { name: 'type', label: 'Type', type: 'select', options: ['Sheep', 'Goat', 'Lambs', 'Whole Family'] },
      { name: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female'] },
      { name: 'weight', label: 'Weight (kg)', type: 'number', icon: <FaWeight /> }
    ]
  }
};

const breedSuggestions = {
  chickens: ['Aseel', 'Kadaknath', 'Country', 'Broiler'],
  pets: ['Labrador', 'Persian Cat', 'German Shepherd', 'Rabbit'],
  cattle: ['Jersey', 'Sahiwal', 'Murrah', 'Gir'],
  'sheep-goat': ['Boer', 'Jamunapari', 'Barbari', 'Sirohi']
};

const PRICE_MIN = 0;
const PRICE_MAX = 100000;

// 1. Full sorted list of Indian states
const INDIAN_STATES = [
  'Andhra Pradesh', 'Telangana', 'Tamil Nadu', 'Karnataka', 'Kerala', 'Maharashtra', 'Delhi', 'Uttar Pradesh', 'West Bengal', 'Gujarat', 'Rajasthan', 'Madhya Pradesh', 'Punjab', 'Haryana', 'Bihar', 'Odisha', 'Assam', 'Jharkhand', 'Chhattisgarh', 'Goa', 'Himachal Pradesh', 'Uttarakhand', 'Tripura', 'Meghalaya', 'Manipur', 'Nagaland', 'Arunachal Pradesh', 'Mizoram', 'Sikkim', 'Ladakh', 'Jammu and Kashmir', 'Puducherry', 'Chandigarh', 'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep' 
];

const FilterSidebar = ({ onFilterApply }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [type, setType] = useState('');
  const [price, setPrice] = useState([PRICE_MIN, PRICE_MAX]);
  const [customFields, setCustomFields] = useState({});
  const [breed, setBreed] = useState('');
  const [age, setAge] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [states, setStates] = useState(INDIAN_STATES);
  const [cities, setCities] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setStates(INDIAN_STATES);
  }, []);

  useEffect(() => {
    if (state) {
      setCities(getCitiesByState(state));
      setCity(''); // Always reset city when state changes
    } else {
      setCities([]);
      setCity('');
    }
  }, [state]);

  // Reset type and custom fields when category changes
  useEffect(() => {
    setType('');
    setCustomFields({});
    setBreed('');
    setAge('');
  }, [selectedCategory]);

  const handleCategoryTab = (cat) => {
    setSelectedCategory(cat);
    setError('');
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const handleCustomFieldChange = (e) => {
    const { name, value } = e.target;
    // Sanitize numeric fields
    const sanitizedValue = name === 'weight' || name === 'age' 
      ? value.replace(/[^0-9.]/g, '') 
      : sanitizeInput(value);
    setCustomFields({ ...customFields, [name]: sanitizedValue });
  };

  const handleBreedChange = (e) => setBreed(sanitizeInput(e.target.value));
  const handleAgeChange = (e) => {
    // Only allow numbers and decimal points for age
    const sanitized = e.target.value.replace(/[^0-9.]/g, '');
    setAge(sanitized);
  };
  const handleStateChange = (e) => setState(e.target.value); // State is from dropdown, safe
  const handleCityChange = (e) => setCity(e.target.value); // City is from dropdown, safe

  const handleReset = () => {
    setSelectedCategory('');
    setType('');
    setPrice([PRICE_MIN, PRICE_MAX]);
    setCustomFields({});
    setBreed('');
    setAge('');
    setState('');
    setCity('');
    setError('');
  };

  // 2. Fuzzy search for Age/Weight
  const getFuzzyRange = (value, type) => {
    if (!value) return {};
    let num = parseFloat(value);
    if (isNaN(num)) return {};
    if (type === 'age') {
      // Age in months/years: allow ±2 months
      return { minAge: Math.max(0, num - 2), maxAge: num + 2 };
    }
    if (type === 'weight') {
      // Weight in kg: allow ±2kg
      return { minWeight: Math.max(0, num - 2), maxWeight: num + 2 };
    }
    return {};
  };

  const handleApply = (e) => {
    e.preventDefault();
    if (!selectedCategory && !type && price[0] === PRICE_MIN && price[1] === PRICE_MAX && !breed && !age && !state && !city && Object.keys(customFields).length === 0) {
      setError('Please select at least one filter option.');
      return;
    }
    setError('');
    const fuzzyAge = getFuzzyRange(age, 'age');
    const fuzzyWeight = getFuzzyRange(customFields.weight, 'weight');
    onFilterApply({
      category: selectedCategory,
      type,
      minPrice: price[0],
      maxPrice: price[1],
      ...customFields,
      breed,
      age,
      state,
      city,
      ...fuzzyAge,
      ...fuzzyWeight
    });
  };

  // 3. Hide Milk Capacity for Bull/Calf
  const shouldShowMilkCapacity =
    !(selectedCategory === 'cattle' && (type === 'Bull' || type === 'Calf'));

  // Get type options from categoryConfigs or fallback
  const typeOptions = (selectedCategory && categoryConfigs[selectedCategory]?.fields.find(f => f.name === 'type')?.options) || [];

  return (
    <Card className="p-4 shadow-sm border-0 rounded-lg filter-sidebar">
      <div className="d-flex align-items-center mb-4">
        <FaFilter className="text-primary me-2" />
        <h5 className="mb-0">Filters</h5>
      </div>
      <Form onSubmit={handleApply}>
        {/* Category Tabs */}
        <div className="mb-4">
          <div className="d-flex justify-content-between mb-2">
            {Object.entries(categoryConfigs).map(([key, cat]) => (
              <OverlayTrigger
                key={key}
                placement="top"
                overlay={<Tooltip>{cat.label}</Tooltip>}
              >
                <Button
                  variant={selectedCategory === key ? 'primary' : 'outline-secondary'}
                  className="d-flex flex-column align-items-center px-2 py-1"
                  onClick={() => handleCategoryTab(key)}
                  style={{ minWidth: 60 }}
                >
                  <span style={{ fontSize: 24 }}>{cat.icon}</span>
                  <span style={{ fontSize: 12 }}>{cat.label.split('/')[0]}</span>
                </Button>
              </OverlayTrigger>
            ))}
          </div>
        </div>

        {/* Price Range Slider (no price above thumb) */}
        <div className="mb-4">
          <Form.Label><FaRupeeSign className="me-2 text-success" />Price Range (₹)</Form.Label>
          <div style={{ margin: '24px 8px 8px 8px' }}>
            <div className="d-flex justify-content-between mb-2">
              <span>Rs. {price[0].toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              <span>Rs. {price[1].toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
            <Range
              step={1}
              min={PRICE_MIN}
              max={PRICE_MAX}
              values={price}
              onChange={setPrice}
              renderTrack={({ props, children }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: '6px',
                    background: 'linear-gradient(90deg, #e0e0e0 0%, #007bff 0%, #007bff 100%, #e0e0e0 100%)',
                    borderRadius: '3px',
                    margin: '0 8px',
                  }}
                >
                  {children}
                </div>
              )}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    height: '22px',
                    width: '22px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    border: '2px solid #007bff',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* Type Selection */}
        {selectedCategory && typeOptions.length > 0 && (
          <Form.Group className="mb-4">
            <Form.Label><FaList className="me-2 text-secondary" />Type</Form.Label>
            <Form.Select value={type} onChange={handleTypeChange}>
              <option value="">Select Type</option>
              {typeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {/* Custom Fields based on Category */}
        {selectedCategory && categoryConfigs[selectedCategory]?.fields.map(field => {
          if (field.name === 'type') return null;
          if (field.name === 'milkCapacity' && !shouldShowMilkCapacity) return null;
          return (
            <Form.Group className="mb-4" key={field.name}>
              <Form.Label>{field.icon} {field.label}</Form.Label>
              {field.type === 'select' ? (
                <Form.Select name={field.name} value={customFields[field.name] || ''} onChange={handleCustomFieldChange}>
                  <option value="">Select {field.label}</option>
                  {field.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type={field.type}
                  name={field.name}
                  value={customFields[field.name] || ''}
                  onChange={handleCustomFieldChange}
                  placeholder={field.label}
                />
              )}
            </Form.Group>
          );
        })}

        {/* Common Fields: Breed and Age */}
        <Form.Group className="mb-4">
          <Form.Label><FaPaw className="me-2 text-secondary" />Breed</Form.Label>
          <Form.Control
            type="text"
            value={breed}
            onChange={handleBreedChange}
            placeholder="Type to get suggestions..."
            list="breed-suggestions"
          />
          <datalist id="breed-suggestions">
            {(breedSuggestions[selectedCategory] || []).map(b => (
              <option key={b} value={b} />
            ))}
          </datalist>
        </Form.Group>
        <Form.Group className="mb-4">
          <Form.Label><FaBirthdayCake className="me-2 text-warning" />Age</Form.Label>
          <Form.Control
            type="text"
            value={age}
            onChange={handleAgeChange}
            placeholder="e.g., 6 months, 2 years"
          />
        </Form.Group>

        {/* Location Fields */}
        <Form.Group className="mb-4">
          <Form.Label><FaMapMarkerAlt className="me-2 text-danger" />Location</Form.Label>
          <Form.Select value={state} onChange={handleStateChange} className="mb-2">
            <option value="">Select State</option>
            {states.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Form.Select>
          <Form.Select value={city} onChange={handleCityChange} disabled={!state}>
            <option value="">Select City</option>
            {cities.map(c => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Error Message */}
        {error && <div className="text-danger mb-3">{error}</div>}

        {/* Action Buttons */}
        <div className="d-flex gap-2">
          <Button type="submit" variant="primary" className="flex-grow-1 rounded-3">
            Apply Filters
          </Button>
          <Button type="button" variant="outline-secondary" onClick={handleReset} className="rounded-3">
            <FaUndo />
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default FilterSidebar;
